import { action, computed, makeObservable, observable } from 'mobx';

import dayjs from 'dayjs';

import { eventBus, subscribe } from 'mobx-event-bus2';

import RootStore from 'stores/Root/Root';
import { timezoneStore } from 'stores/Timezone';
import API from 'utils/api';
import { HomeResponse } from 'utils/api/modules/common/common.types';
import { AcknowledgementState } from 'utils/api/modules/entities/entities.types';
import { TasksSummary } from 'utils/api/modules/tasks/tasks.types';
import { EventType } from 'utils/events/constants';
import { readerDayKey, readerPeriod, utcMidnight } from 'utils/formatters/timezone';

// Actionable audit instances only. Upcoming is excluded on purpose: a recurring audit would
// otherwise inflate the count without bound (only the current actionable instance counts).
const OPEN_AUDIT_STATUSES = 'todo;;in_progress;;overdue';
const IN_PROGRESS_AUDIT_STATUS = 'in_progress';
const COMPLETED_AUDIT_STATUS = 'completed';

// Mirrors backend `view_horizon()` in schedules/views.py: a 2020-01-01 floor up to 3 months
// from now. Required because `/schedules/tasks/?type=audit&withPagination=true` rejects
// requests without a `dueDate` range.
const wideAuditDueDateRange = (): string => {
  const start = Date.UTC(2020, 0, 1);
  const end = dayjs().add(3, 'month').valueOf();

  return `${start}::${end}`;
};

// Backend uses a Monday-start ISO week (`today.weekday() == 0` on Monday in Python).
// dayjs().day() returns 0 for Sunday, so shift to (0 = Monday .. 6 = Sunday).
const weekDueDateRange = (): string => {
  const now = dayjs();
  const dayIndex = (now.day() + 6) % 7;
  const monday = now.subtract(dayIndex, 'day').startOf('day');
  const sunday = monday.add(6, 'day').endOf('day');

  return `${monday.valueOf()}::${sunday.valueOf()}`;
};

// The audits endpoint compares instants; the acknowledgement filter reads each bound as a calendar day
const readerWeek = (): { instants: string; days: string } => {
  const { from, to } = readerPeriod('isoWeek');
  return {
    instants: `${from}::${to}`,
    days: `${utcMidnight(readerDayKey(from))}::${utcMidnight(readerDayKey(to))}`,
  };
};

type AcknowledgementsSummary = {
  assignedToUser: number;
  dueThisWeek: number;
  completedThisWeek: number;
};

export default class HomeStore {
  store: RootStore;

  api: typeof API;

  @observable isLoading = false;

  @observable content?: HomeResponse;

  @observable auditsSummary?: TasksSummary;

  @observable acknowledgementsSummary?: AcknowledgementsSummary;

  @observable hasError = false;

  constructor(rootStore: RootStore, api: typeof API) {
    this.store = rootStore;
    this.api = api;

    makeObservable(this);
    eventBus.register(this);
  }

  // The four Home tiles: tasks + scheduled audits + acknowledgements summed per tile.
  // Acknowledgements are binary (no in-progress state), so they never feed `inProgress`.
  @computed get actionsSummary(): TasksSummary | undefined {
    const tasks = this.content?.tasksSummary;

    if (tasks === undefined) {
      return undefined;
    }

    const audits = this.auditsSummary;
    const acknowledgements = this.acknowledgementsSummary;

    return {
      assignedToUser: tasks.assignedToUser + (audits?.assignedToUser ?? 0) + (acknowledgements?.assignedToUser ?? 0),
      inProgress: tasks.inProgress + (audits?.inProgress ?? 0),
      dueThisWeek: tasks.dueThisWeek + (audits?.dueThisWeek ?? 0) + (acknowledgements?.dueThisWeek ?? 0),
      completedThisWeek:
        tasks.completedThisWeek + (audits?.completedThisWeek ?? 0) + (acknowledgements?.completedThisWeek ?? 0),
    };
  }

  private hasAuditsLicense(): boolean {
    const user = this.store.session?.user;

    return Boolean(user?.licenses?.workflows && user?.scheduleEnabled);
  }

  private hasAcknowledgements(): boolean {
    const user = this.store.session?.user;

    return Boolean(user?.documentAcknowledgementsEnabled && user?.licenses?.documents);
  }

  private async loadAuditsSummary(userUuid: string): Promise<TasksSummary> {
    const wideRange = wideAuditDueDateRange();
    const weekRange = timezoneStore.enabled ? readerWeek().instants : weekDueDateRange();
    const sharedParams = { assignedTo: userUuid, limit: 1 };

    const [assignedToUser, inProgress, dueThisWeek, completedThisWeek] = await Promise.all([
      this.api.schedules.loadAuditTasks({ ...sharedParams, status: OPEN_AUDIT_STATUSES, dueDate: wideRange })(),
      this.api.schedules.loadAuditTasks({ ...sharedParams, status: IN_PROGRESS_AUDIT_STATUS, dueDate: wideRange })(),
      // strictDueDate opts out of the endpoint's overdue-outside-window union (the Actions
      // grid wants that union; a week count must not include every overdue audit ever).
      this.api.schedules.loadAuditTasks({
        ...sharedParams,
        status: OPEN_AUDIT_STATUSES,
        dueDate: weekRange,
        strictDueDate: true,
      })(),
      // The audits endpoint only filters by due date, so "completed this week" is
      // approximated as completed instances due this week.
      this.api.schedules.loadAuditTasks({ ...sharedParams, status: COMPLETED_AUDIT_STATUS, dueDate: weekRange })(),
    ]);

    return {
      assignedToUser: assignedToUser.data.count ?? 0,
      inProgress: inProgress.data.count ?? 0,
      dueThisWeek: dueThisWeek.data.count ?? 0,
      completedThisWeek: completedThisWeek.data.count ?? 0,
    };
  }

  private async loadAcknowledgementsSummary(): Promise<AcknowledgementsSummary> {
    const weekRange = timezoneStore.enabled ? readerWeek().days : weekDueDateRange();
    // The days are the reader's, so the server must read acknowledgedAt in that zone, not resolve its own
    const readerZone = timezoneStore.enabled ? { time_zone: timezoneStore.readerZone } : {};

    const [assignedToUser, dueThisWeek, completedThisWeek] = await Promise.all([
      this.api.entities.listMyAcknowledgements({ state: AcknowledgementState.Pending, limit: 1 })(),
      this.api.entities.listMyAcknowledgements({
        state: AcknowledgementState.Pending,
        dueDate: weekRange,
        limit: 1,
      })(),
      this.api.entities.listMyAcknowledgements({
        state: AcknowledgementState.Acknowledged,
        acknowledgedAt: weekRange,
        limit: 1,
        ...readerZone,
      })(),
    ]);

    return {
      assignedToUser: assignedToUser.data.count ?? 0,
      dueThisWeek: dueThisWeek.data.count ?? 0,
      completedThisWeek: completedThisWeek.data.count ?? 0,
    };
  }

  @action.bound
  async loadHomeContent(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    const userUuid = this.store.session?.user?.uuid;
    const shouldLoadAudits = Boolean(userUuid) && this.hasAuditsLicense();
    const shouldLoadAcknowledgements = this.hasAcknowledgements();

    try {
      const [tasksResult, auditsResult, acknowledgementsResult] = await Promise.allSettled([
        this.api.loadHomeContent()(),
        shouldLoadAudits ? this.loadAuditsSummary(userUuid as string) : Promise.resolve(undefined),
        shouldLoadAcknowledgements ? this.loadAcknowledgementsSummary() : Promise.resolve(undefined),
      ]);

      if (tasksResult.status === 'rejected') {
        throw tasksResult.reason;
      }

      this.content = tasksResult.value.data;
      this.auditsSummary = auditsResult.status === 'fulfilled' ? auditsResult.value : undefined;
      this.acknowledgementsSummary =
        acknowledgementsResult.status === 'fulfilled' ? acknowledgementsResult.value : undefined;

      if (auditsResult.status === 'rejected' || acknowledgementsResult.status === 'rejected') {
        this.store.notification.enqueueErrorSnackbar(`Could not load all action counts. Please try again later`);
      }
    } catch (error) {
      this.hasError = true;
      this.store.notification.enqueueErrorSnackbar(`Could not load dashboard details. Please try again later`);
    } finally {
      this.isLoading = false;
    }
  }

  @action.bound
  @subscribe(EventType.DeletedTask)
  @subscribe(EventType.CreatedTask)
  reset(): void {
    this.content = undefined;
    this.auditsSummary = undefined;
    this.acknowledgementsSummary = undefined;
    this.hasError = false;
  }
}
